import type { ReactNode } from 'react';

import { cn } from './cn';
import { CheckIcon, DangerIcon, InfoIcon, WarningIcon } from './icons';

/**
 * The inline banner. Four kinds, each carrying **an icon and text** — two kinds
 * are never told apart by a coloured border alone.
 *
 * `role="alert"` only on the critical kinds: an informational banner that
 * interrupts a screen reader for no reason is a defect of its own.
 */
export type AlertKind = 'info' | 'success' | 'warning' | 'danger';

const KIND_CLASSES: Record<AlertKind, string> = {
  info: 'border-info bg-info-soft text-ink',
  success: 'border-success bg-success-soft text-ink',
  warning: 'border-warning bg-warning-soft text-ink',
  danger: 'border-danger bg-danger-soft text-ink',
};

const KIND_ICONS: Record<AlertKind, typeof InfoIcon> = {
  info: InfoIcon,
  success: CheckIcon,
  warning: WarningIcon,
  danger: DangerIcon,
};

const KIND_ICON_CLASSES: Record<AlertKind, string> = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
};

export function Alert({
  kind = 'info',
  title,
  children,
  className,
}: {
  kind?: AlertKind;
  title?: string;
  children: ReactNode;
  className?: string;
}) {
  const Glyph = KIND_ICONS[kind];
  const critical = kind === 'danger';

  return (
    <div
      {...(critical ? { role: 'alert' } : {})}
      className={cn(
        'flex items-start gap-3 rounded-md border-s-4 border-border p-4 text-sm',
        KIND_CLASSES[kind],
        className,
      )}
    >
      <Glyph className={cn('mt-1 shrink-0', KIND_ICON_CLASSES[kind])} />
      <div className="min-w-0">
        {title ? <p className="font-semibold">{title}</p> : null}
        <div className="text-ink-muted">{children}</div>
      </div>
    </div>
  );
}
